import React from 'react';
import DOMPurify from 'dompurify';
import styled from 'styled-components';

/**
 * [T54255] Renders a Phabricator-rendered description (HTML produced by
 * conduit remarkup.process, context 'maniphest') inside the planning
 * screens.
 *
 * - The HTML comes from the BACKEND (Phabricator itself renders it), so
 *   it is pixel-identical to the task page — no client-side Remarkup
 *   parser.
 * - Phabricator escapes user text in its output, but this is still
 *   user-authored markup stored in our DB: sanitize with DOMPurify before
 *   injecting (defence in depth).
 * - Minimal styling for Phabricator's remarkup-* classes (lists, headers,
 *   code blocks, tables, links) to match the light planning theme.
 */

const RemarkupWrap = styled.div`
    font-size: 13px;
    line-height: 1.55;
    color: #1a1d23;
    word-break: break-word;

    /* Headers */
    h1, h2, h3, h4, h5, h6 {
        margin: 12px 0 6px;
        color: #1a1d23;
        font-weight: 600;
        line-height: 1.3;
    }
    h1 { font-size: 17px; }
    h2 { font-size: 15.5px; }
    h3 { font-size: 14px; }
    h4, h5, h6 { font-size: 13px; }

    /* Paragraphs and text */
    p { margin: 6px 0; }
    strong { font-weight: 600; }
    em { font-style: italic; }

    /* Links */
    a {
        color: #2185d0;
        text-decoration: none;
        &:hover { text-decoration: underline; }
    }

    /* Lists — Phabricator emits ol/ul with remarkup classes */
    ul, ol {
        margin: 6px 0;
        padding-left: 22px;
    }
    li { margin: 2px 0; }

    /* Code blocks (fenced) and inline code */
    pre {
        background: #f8f9fa;
        border: 1px solid #e0e3e8;
        border-radius: 4px;
        padding: 10px 12px;
        overflow-x: auto;
        margin: 8px 0;
        font-size: 12px;
        line-height: 1.5;
    }
    code {
        font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
        font-size: 12px;
    }
    :not(pre) > code {
        background: #f1f2f4;
        border-radius: 3px;
        padding: 1px 5px;
    }

    /* Blockquotes */
    blockquote {
        margin: 8px 0;
        padding: 4px 12px;
        border-left: 3px solid #c5cad2;
        color: #5c6470;
        background: #fafbfc;
    }

    /* Tables */
    table {
        border-collapse: collapse;
        margin: 8px 0;
        font-size: 12.5px;
    }
    th, td {
        border: 1px solid #e0e3e8;
        padding: 5px 10px;
        text-align: left;
        vertical-align: top;
    }
    th {
        background: #f8f9fa;
        font-weight: 600;
    }

    /* Horizontal rule */
    hr {
        border: none;
        border-top: 1px solid #e0e3e8;
        margin: 12px 0;
    }

    /* Inline images (Phabricator file embeds) */
    img {
        max-width: 100%;
        border-radius: 4px;
    }
`;

interface RemarkupContentProps {
    /** HTML from backend PlanningTicket.descriptionHtml */
    html: string;
    className?: string;
    'data-test'?: string;
}

/** Sanitize once per render — DOMPurify strips scripts/handlers, keeps the remarkup tags. */
export function sanitizeRemarkup(html: string): string {
    return DOMPurify.sanitize(html || '', {
        ALLOWED_TAGS: [
            'a', 'b', 'strong', 'i', 'em', 'u', 's', 'del', 'strike',
            'p', 'br', 'hr', 'span', 'div',
            'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
            'ul', 'ol', 'li',
            'blockquote', 'pre', 'code',
            'table', 'thead', 'tbody', 'tr', 'th', 'td',
            'img',
        ],
        ALLOWED_ATTR: ['href', 'title', 'target', 'rel', 'src', 'alt', 'class', 'colspan', 'rowspan'],
        ALLOW_DATA_ATTR: false,
    });
}

export default class RemarkupContent extends React.Component<RemarkupContentProps> {
    render() {
        const clean = sanitizeRemarkup(this.props.html);
        return (
            <RemarkupWrap
                className={this.props.className}
                data-test={this.props['data-test']}
                dangerouslySetInnerHTML={{ __html: clean }}
            />
        );
    }
}
